import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Coins,
  FileText,
  Globe2,
  Sparkles,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import type {
  CountryServicePair,
  PairContent,
} from "@/content/country-service/builder";
import { COUNTRIES as COUNTRY_HUBS } from "@/content/countries/registry";
import { pickReciprocal } from "@/lib/nearby-links";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";

export const Route = createFileRoute("/countries/$country/$service")({
  component: CountryServicePage,
  loader: async ({
    params,
  }): Promise<{ pair: CountryServicePair; content: PairContent }> => {
    const { resolvePair, buildPairContent } = await import(
      "@/content/country-service/builder"
    );
    const pair = resolvePair(params.country, params.service);
    if (!pair) throw notFound();
    return { pair, content: buildPairContent(pair) };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
        <p className="mt-3 text-muted-foreground">
          ลองดูประเทศและบริการอื่นที่เราดูแลได้
        </p>
        <Link
          to="/countries"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> กลับไปรายการประเทศ
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background p-12 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  head: ({ params, loaderData }) => {
    const pair = loaderData?.pair;
    const content = loaderData?.content;
    const site = getActiveSiteUrl();
    if (!pair || !content) {
      return pageHeadForProvince({
        title: "ไม่พบหน้า — Thai Notary",
        description: "ไม่พบหน้านี้",
        path: `/countries/${params.country}/${params.service}`,
      });
    }
    const base = pageHeadForProvince({
      title: content.metaTitle,
      description: content.metaDesc,
      path: `/countries/${pair.country.slug}/${pair.service.slug}`,
      type: "article",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ประเทศปลายทาง", url: `${site}/countries` },
              {
                name: pair.country.nameTh,
                url: `${site}/countries/${pair.country.slug}`,
              },
              {
                name: pair.service.h1,
                url: `${site}/countries/${pair.country.slug}/${pair.service.slug}`,
              },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(content.faqs)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildHowToStepsSchema({
              name: `ขั้นตอน: ${content.h1}`,
              description: content.metaDesc,
              path: `/countries/${pair.country.slug}/${pair.service.slug}`,
              steps: content.process,
              inLanguage: "th",
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/countries/${pair.country.slug}/${pair.service.slug}#service`,
            name: content.h1,
            description: content.metaDesc,
            provider: { "@id": `${site}/#organization` },
            areaServed: [
              { "@type": "Country", name: pair.country.nameEn },
              { "@type": "City", name: "Phuket" },
            ],
            serviceType: pair.service.h1,
            url: `${site}/countries/${pair.country.slug}/${pair.service.slug}`,
            ...(pair.service.pricing?.length
              ? {
                  offers: {
                    "@type": "AggregateOffer",
                    priceCurrency: "THB",
                    lowPrice: (pair.service.pricing[0]?.price ?? "")
                      .replace(/[^\d]/g, "") || undefined,
                    offerCount: pair.service.pricing.length,
                    availability: "https://schema.org/InStock",
                  },
                  priceRange: "฿฿",
                }
              : {}),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/countries/${params.country}/${params.service}`)) },
      ],
    };
  },
});

function CountryServicePage() {
  const { pair, content: c } = Route.useLoaderData() as {
    pair: CountryServicePair;
    content: PairContent;
  };
  const { country, service } = pair;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        {/* Breadcrumb */}
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/countries" className="hover:text-primary">ประเทศ</Link>
            <span className="mx-2">/</span>
            <Link
              to="/countries/$country"
              params={{ country: country.slug }}
              className="hover:text-primary"
            >
              {country.flag} {country.nameTh}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{c.h1}</span>
          </div>
        </nav>

        {/* Hero */}
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <Sparkles className="h-3.5 w-3.5" />
              ปลายทาง {country.flag} {country.nameTh}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight md:text-4xl">
              {c.h1}
            </h1>
            <p
              id="ai-answer-tldr"
              className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed text-foreground"
            >
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                สรุปสั้น
              </span>
              {c.tldr}
            </p>
          </div>
        </section>

        {/* Quick facts */}
        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            {c.quickFacts.map((f) => (
              <div
                key={f.label}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {f.label}
                </div>
                <div className="mt-1 text-sm font-bold text-foreground">
                  {f.value}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why this country */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Globe2 className="h-5 w-5 text-gold" />
              ทำไมเคส {country.nameTh} ถึงต้องใช้ทีมที่เชี่ยวชาญเฉพาะ
            </h2>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-muted-foreground">
              {c.whyThisCountry.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Clock className="h-5 w-5 text-gold" />
              ขั้นตอนทำงาน — ภูเก็ต → MFA → {country.nameTh}
            </h2>
            <ol className="mt-6 space-y-4">
              {c.process.map((s) => (
                <li
                  key={s.step}
                  className="flex gap-4 rounded-xl border border-border bg-card p-5"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {s.step}
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{s.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {s.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Documents */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <FileText className="h-5 w-5 text-gold" />
              เอกสารที่ต้องเตรียม
            </h2>
            <ul className="mt-4 grid gap-2 md:grid-cols-2">
              {c.documents.map((d, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Price snapshot */}
        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Coins className="h-5 w-5 text-gold" />
              ราคาอ้างอิง
            </h2>
            <div className="mt-4 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left">รายการ</th>
                    <th className="px-4 py-2 text-left">ราคา</th>
                  </tr>
                </thead>
                <tbody>
                  {service.pricing.slice(0, 6).map((p: { item: string; price: string }, i: number) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-2">{p.item}</td>
                      <td className="px-4 py-2 font-bold">{p.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              ราคารวมโดยประมาณ: {country.costRange} — ขึ้นกับจำนวนเอกสารและประเภทการรับรอง
            </p>
          </div>
        </section>

        {/* FAQs */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย</h2>
            <div className="mt-4 space-y-3">
              {c.faqs.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-border bg-card p-4"
                >
                  <summary className="cursor-pointer list-none font-bold text-foreground">
                    <span className="mr-2 text-gold">Q.</span>
                    {f.q}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Related */}
        {c.related.length > 0 && (
          <section className="border-b border-border bg-muted/20">
            <div className="mx-auto max-w-4xl px-4 py-12">
              <h2 className="text-xl font-bold">
                บริการอื่นสำหรับ {country.nameTh} {country.flag}
              </h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {c.related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/countries/$country/$service"
                    params={{ country: country.slug, service: r.slug }}
                    className="rounded-xl border border-border bg-card p-4 text-sm font-semibold transition hover:border-gold hover:bg-card/80"
                  >
                    {r.label} →
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <Link
                  to="/countries/$country"
                  params={{ country: country.slug }}
                  className="text-sm font-bold text-primary hover:underline"
                >
                  ← กลับไปหน้าหลัก {country.nameTh}
                </Link>
              </div>
            </div>
          </section>
        )}

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="text-xl font-bold">
              {service.h1} สำหรับประเทศปลายทางอื่นในภูมิภาค {country.region}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              ขั้นตอนรับรองของประเทศในภูมิภาคเดียวกันมักใช้ชุดเอกสารใกล้เคียงกัน ถ้าคุณต้องยื่นหลายประเทศ
              เปรียบเทียบได้จาก
              <Link to="/countries" className="mx-1 font-semibold text-primary hover:underline">
                ประเทศปลายทางทั้งหมด
              </Link>
              และ
              <Link to="/embassies" className="mx-1 font-semibold text-primary hover:underline">
                สถานทูต &amp; สถานกงสุลในไทย
              </Link>
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {pickReciprocal(COUNTRY_HUBS, country, (x) => x.region, 5).map((n) => (
                <Link
                  key={n.slug}
                  to="/countries/$country/$service"
                  params={{ country: n.slug, service: service.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition hover:border-gold"
                >
                  <span className="font-semibold">
                    {n.flag} {service.h1} {n.nameTh}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{n.region}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">{c.finalCta}</h2>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground"
              >
                โทร {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener"
                className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold"
              >
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="consular" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}